import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  getProducts,
  deleteProduct,
  updateProduct,
  createProduct,
  getFamilies,
} from "../api";
import { AuthContext } from "../AuthContext";
import { Pencil, Trash2 } from "lucide-react";

export default function Products() {
  const { user } = useContext(AuthContext);

  const [products, setProducts] = useState([]);
  const [families, setFamilies] = useState([]);

  const [form, setForm] = useState({
    codigo: "",
    referencia: "",
    familia: "",
    descricao: "",
  });

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [msg, setMsg] = useState(null);

  // 🔹 Buscar Produtos
  const fetchProducts = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await getProducts(user.token);
      setProducts(data);
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoadingList(false);
    }
  }, [user.token]);

  // 🔹 Buscar Famílias (para o select)
  const fetchFamilies = useCallback(async () => {
    try {
      const data = await getFamilies(user.token);
      setFamilies(data);
    } catch (error) {
      setMsg({ type: "error", text: "Erro ao carregar famílias." });
    }
  }, [user.token]);

  useEffect(() => {
    fetchFamilies();
    fetchProducts();
  }, [fetchFamilies, fetchProducts]);

  // 🔹 Criar / Atualizar Produto
  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(null);

    if (!form.codigo || !form.referencia || !form.familia || !form.descricao) {
      setMsg({ type: "error", text: "Preencha todos os campos obrigatórios." });
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await updateProduct(editingId, user.token, form);
        setMsg({ type: "success", text: "Produto atualizado com sucesso!" });
      } else {
        await createProduct(user.token, form);
        setMsg({ type: "success", text: "Produto cadastrado com sucesso!" });
      }

      // Limpa formulário
      setForm({
        codigo: "",
        referencia: "",
        familia: "",
        descricao: "",
      });
      setEditingId(null);
      fetchProducts(); // 🔥 atualiza lista
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }

  // 🔹 Editar
  function handleEdit(product) {
    setForm({
      codigo: product.codigo,
      referencia: product.referencia,
      familia: product.familia,
      descricao: product.descricao,
    });
    setEditingId(product.id);
    setMsg(null);
  }

  function handleCancelEdit() {
    setEditingId(null);
    setForm({
      codigo: "",
      referencia: "",
      familia: "",
      descricao: "",
    });
  }

  // 🔹 Deletar
  async function handleDelete(id) {
    if (!window.confirm("Deseja excluir este produto?")) return;

    try {
      await deleteProduct(user.token, id);
      setMsg({ type: "success", text: "Produto excluído com sucesso!" });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    }
  }

  // Atualiza campo do formulário
  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card" style={{ maxWidth: "1100px", margin: "0 auto" }}>

          {/* Cabeçalho */}
          <div style={{ 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "2rem" 
          }}>
            <h3>{editingId ? "Editar Produto" : "Novo Produto"}</h3>
          </div>

          {/* ==================== FORMULÁRIO ==================== */}
          <div className="item-row add-row" style={{ marginBottom: "2.5rem", gridTemplateColumns: "1fr 1fr 1fr" }}>
            
            <div className="item-input">
              <label>Código</label>
              <input
                type="text"
                name="codigo"
                value={form.codigo}
                onChange={handleChange}
                placeholder="Código"
                className="input-text"
              />
            </div>

            <div className="item-input">
              <label>Referência</label>
              <input
                type="text"
                name="referencia"
                value={form.referencia}
                onChange={handleChange}
                placeholder="Referência"
                className="input-text"
              />
            </div>

            <div className="item-input">
              <label>Família</label>
              <select
                name="familia"
                value={form.familia}
                onChange={handleChange}
                className="input-text"
              >
                <option value="">Selecione uma família...</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.familia}
                  </option>
                ))}
              </select>
            </div>

            <div className="item-input">
              <label>Descrição</label>
              <input
                type="text"
                name="descricao"
                value={form.descricao}
                onChange={handleChange}
                placeholder="Descrição do produto"
                className="input-text"
              />
            </div>

            <div className="item-actions">
              <button 
                type="button"
                className="btn-small"
                onClick={handleSubmit}
                disabled={loading || !form.codigo || !form.referencia || !form.familia || !form.descricao}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ marginRight: "8px" }}></span>
                    Salvando...
                  </>
                ) : editingId ? (
                  "Salvar Alterações"
                ) : (
                  "Cadastrar Produto"
                )}
              </button>

              {editingId && (
                <button 
                  type="button" 
                  className="btn-small"
                  onClick={handleCancelEdit}
                >
                  Cancelar
                </button>
              )}
            </div>
          </div>

          {/* Mensagem de feedback */}
          {msg && (
            <div 
              className={msg.type === "error" ? "error-msg" : "success-msg"} 
              style={{ marginBottom: "2rem" }}
            >
              {msg.text}
            </div>
          )}

          {/* ==================== LISTA DE PRODUTOS ==================== */}
          <div style={{ marginTop: "2rem" }}>
            <h3 style={{ marginBottom: "1.5rem" }}>Lista de Produtos</h3>

            {loadingList ? (
              <div className="center-content" style={{ padding: "3rem 0" }}>
                <div className="spinner"></div>
                <p>Carregando produtos...</p>
              </div>
            ) : (
              <div className="items-list">
                {products.length === 0 ? (
                  <p style={{ 
                    opacity: 0.7, 
                    textAlign: "center", 
                    padding: "3rem 0",
                    fontSize: "1.05rem"
                  }}>
                    Nenhum produto cadastrado ainda.
                  </p>
                ) : (
                  products
                    .sort((a, b) => b.id - a.id)
                    .map((p) => (
                      <div 
                        key={p.id}
                        className="item-row"
                        style={{
                          background: editingId === p.id ? "rgba(59, 130, 246, 0.12)" : undefined,
                          border: editingId === p.id ? "1px solid #3b82f6" : undefined,
                          gridTemplateColumns: "2fr 2fr 2fr"
                        }}
                      >
                        <div className="item-input">
                          <label>Código</label>
                          <input value={p.codigo} disabled />
                        </div>

                        <div className="item-input">
                          <label>Referência</label>
                          <input value={p.referencia} disabled />
                        </div>

                        <div className="item-input">
                          <label>Família</label>
                          <input 
                            value={families.find(f => f.id === p.familia)?.familia || "-"} 
                            disabled 
                          />
                        </div>

                        <div className="item-input">
                          <label>Descrição</label>
                          <input value={p.descricao} disabled />
                        </div>

                        <div className="item-actions">
                          <button
                            className="btn-small"
                            onClick={() => handleEdit(p)}
                            title="Editar Produto"
                          >
                            <Pencil size={18} />
                          </button>

                          <button
                            className="btn-danger btn-small"
                            onClick={() => handleDelete(p.id)}
                            title="Excluir Produto"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}